import baseApi from "./RootBaseApi";
import {
  AdoptionReportNormalized,
  AdoptionReportRawResponse,
  HistoricalTrendsFilters,
  SubscriptionActiveSnapshotItem,
  SubscriptionHistoryItem,
} from "@/types/historical-trends.types";

export const HistoricalTrendsService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSubscriptionAdoptionReport: builder.query<
      AdoptionReportNormalized,
      Partial<
        Pick<
          HistoricalTrendsFilters,
          | "start_date"
          | "end_date"
          | "from"
          | "to"
          | "aggregation"
          | "groupBy"
          | "subscription_ids"
          | "subscription_types"
          | "page"
          | "limit"
        >
      >
    >({
      query: (params) => ({
        url: "admin/reports/subscriptions/adoption",
        method: "GET",
        params,
      }),
      transformResponse: (response: unknown): AdoptionReportNormalized => {
        const raw = (response as AdoptionReportRawResponse)?.data
          ? (response as AdoptionReportRawResponse).data
          : (response as unknown as AdoptionReportRawResponse["data"]);
        const snapshotSrc = (raw as { activeSnapshot?: unknown })
          ?.activeSnapshot as
          | ({ items?: SubscriptionActiveSnapshotItem[] } & {
              totalItems?: number;
              currentPage?: number;
              pageLimit?: number;
              totalPages?: number;
            })
          | SubscriptionActiveSnapshotItem[]
          | undefined;
        const snapshotArr: SubscriptionActiveSnapshotItem[] = Array.isArray(
          (snapshotSrc as { items?: SubscriptionActiveSnapshotItem[] })?.items,
        )
          ? (snapshotSrc as { items: SubscriptionActiveSnapshotItem[] }).items
          : Array.isArray(snapshotSrc)
            ? (snapshotSrc as SubscriptionActiveSnapshotItem[])
            : [];
        const snapshot: SubscriptionActiveSnapshotItem[] = snapshotArr.map(
          (s) => ({
            plan_name: s.plan_name ?? "",
            subscription_type: String(s.subscription_type ?? ""),
            active_count: Number(s.active_count ?? 0),
          }),
        );
        const snapshotPagination =
          snapshotSrc &&
          typeof snapshotSrc === "object" &&
          !Array.isArray(snapshotSrc)
            ? (() => {
                const sp = snapshotSrc as {
                  totalItems?: number;
                  currentPage?: number;
                  pageLimit?: number;
                  totalPages?: number;
                };
                return {
                  totalItems: Number(sp.totalItems ?? 0),
                  currentPage: Number(sp.currentPage ?? 1),
                  pageLimit: Number(sp.pageLimit ?? (snapshotArr.length || 0)),
                  totalPages: Number(sp.totalPages ?? 1),
                };
              })()
            : undefined;
        const historySrc = (raw as { history?: unknown })?.history as
          | { items?: SubscriptionHistoryItem[] }
          | SubscriptionHistoryItem[]
          | undefined;
        const historyArr: SubscriptionHistoryItem[] = Array.isArray(
          (historySrc as { items?: SubscriptionHistoryItem[] })?.items,
        )
          ? (historySrc as { items: SubscriptionHistoryItem[] }).items
          : Array.isArray(historySrc)
            ? (historySrc as SubscriptionHistoryItem[])
            : [];
        const history: SubscriptionHistoryItem[] = historyArr.map((h) => ({
          plan_name: h.plan_name ?? "",
          subscription_type: String(h.subscription_type ?? ""),
          period: h.period ?? "",
          new_count: Number(h.new_count ?? 0),
          cancellations: Number(h.cancellations ?? 0),
          net: Number(h.net ?? 0),
          period_start: h.period_start ?? "",
        }));

        return {
          snapshot,
          history,
          snapshotPagination,
        } as AdoptionReportNormalized;
      },
      providesTags: ["HistoricalTrends"],
      keepUnusedDataFor: 0,
    }),

    getSubscriptionAdoptionCsv: builder.query<
      Blob,
      Partial<
        Pick<
          HistoricalTrendsFilters,
          | "start_date"
          | "end_date"
          | "from"
          | "to"
          | "aggregation"
          | "groupBy"
          | "subscription_ids"
          | "subscription_types"
        > & { isCsv?: boolean }
      >
    >({
      query: (params) => ({
        url: "admin/reports/subscriptions/adoption",
        method: "GET",
        params: { ...(params || {}), isCsv: true },
        responseType: "blob",
      }),
      // blob is returned directly by baseQuery
      transformResponse: (response: unknown): Blob => response as Blob,
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetSubscriptionAdoptionReportQuery,
  useLazyGetSubscriptionAdoptionCsvQuery,
} = HistoricalTrendsService;
